import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ICompany } from '../companies/models/company.interface';
import { CompanyService } from '../companies/services/company.service';
import { CatalogoProdutosService } from '../catalogo-produtos/services/catalogo-produtos.service';
import { IServerCatalogItem } from '../catalogo-produtos/models/catalog';
import { SkincareQuestionnaireService } from '../questionario-skincare/services/skincare-questionnaire.service';
import { ISkincareQuestion } from '../questionario-skincare/models/skincare-question.interface';
import { EMessageType } from '../../shared/data/response-default.interface';
import { SessionService } from '../../shared/services/session.service';
import { ToastService } from '../../shared/services/toast.service';
import { IKit, IKitMatrix, createEmptyKitMatrix } from './models/kit-matrix.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';

type OptionMapKind = 'need' | 'routine' | 'age';

/** Listas de produtos de um kit editadas pelo mesmo seletor. */
type KitProductList = 'productIds' | 'morning' | 'night';

/** Campos editados no JSON, com `true` para os que são lista. */
const ROUTING_FIELDS = { columnSignals: true, signals: false, needs: false, swaps: true } as const;
type RoutingField = keyof typeof ROUTING_FIELDS;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

interface IQuestionOptionChoice {
  optionId: string;
  label: string;
}

@Component({
  selector: 'app-motor-kits-skincare',
  templateUrl: './motor-kits-skincare.component.html',
  styleUrls: ['./motor-kits-skincare.component.scss']
})
export class MotorKitsSkincareComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  companyControl = new UntypedFormControl(null, [Validators.required]);
  companies: ICompany[] = [];

  /** Configuração em edição. Nula = empresa usa o motor padrão (scoring). */
  kitMatrix: IKitMatrix | null = null;

  catalogProducts: IServerCatalogItem[] = [];
  questionOptions: IQuestionOptionChoice[] = [];

  readonly kitProductLists: { key: KitProductList; label: string }[] = [
    { key: 'productIds', label: 'Produtos, na ordem de aplicação (pode ficar vazio se usar Diurna/Noturna)' },
    { key: 'morning', label: 'Diurna (opcional), na ordem de aplicação' },
    { key: 'night', label: 'Noturna (opcional), na ordem de aplicação' }
  ];

  /** Texto do campo JSON de sinais, necessidades e trocas. Vazio = sem esses campos. */
  routingJson = '';
  routingJsonError: string | null = null;

  isLoading = false;
  isSaving = false;
  isRemoving = false;

  /** Problemas devolvidos pelo backend na tentativa de salvar. */
  validationProblems: string[] = [];

  constructor(
    private readonly companyService: CompanyService,
    private readonly catalogoProdutosService: CatalogoProdutosService,
    private readonly questionnaireService: SkincareQuestionnaireService,
    private readonly sessionService: SessionService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.companyService.listarEmpresasAtivas().subscribe((response) => {
      this.companies = response.data || [];
    });

    const sessionCompanyId = this.sessionService.getSession()?.companyId;
    if (sessionCompanyId) {
      this.companyControl.setValue(sessionCompanyId);
      this.companyControl.disable({ emitEvent: false });
      this.loadCompany(String(sessionCompanyId));
    }
  }

  get selectedCompanyId(): string {
    return String(this.companyControl.value || '');
  }

  get usesKitEngine(): boolean {
    return this.kitMatrix !== null;
  }

  get needs(): string[] {
    return Object.keys(this.kitMatrix?.matrix || {});
  }

  onCompanySelectionChanged(companyId: string | null): void {
    this.setKitMatrix(null);
    this.catalogProducts = [];
    this.questionOptions = [];
    this.validationProblems = [];

    if (companyId) {
      this.loadCompany(String(companyId));
    }
  }

  // ---- carga ----

  private loadCompany(companyId: string): void {
    this.isLoading = true;
    this.validationProblems = [];

    // Resposta atrasada de uma empresa trocada antes de voltar nao pode
    // sobrescrever a tela da empresa atual — o save() grava no companyId
    // selecionado, entao um kitMatrix de outra empresa aqui viraria salvo
    // no arquivo errado.
    const isStale = (): boolean => this.selectedCompanyId !== companyId;

    this.catalogoProdutosService.listarKitMatrix(companyId).subscribe({
      next: (response) => {
        if (isStale()) {
          return;
        }
        this.setKitMatrix(response?.data ?? null);
        this.isLoading = false;
      },
      error: () => {
        if (isStale()) {
          return;
        }
        this.setKitMatrix(null);
        this.isLoading = false;
        this.toastService.toast('Não foi possível carregar a configuração de kits desta empresa.', EMessageType.Error);
      }
    });

    this.catalogoProdutosService.listarCatalogoPorCompanyId(companyId).subscribe({
      next: (response) => {
        if (isStale()) {
          return;
        }
        this.catalogProducts = response?.data?.catalog || [];
      },
      error: () => {
        if (isStale()) {
          return;
        }
        this.catalogProducts = [];
      }
    });

    this.questionnaireService.getActive(companyId).subscribe({
      next: (questions) => {
        if (isStale()) {
          return;
        }
        this.questionOptions = this.flattenOptions(questions || []);
      },
      error: () => {
        if (isStale()) {
          return;
        }
        this.questionOptions = [];
      }
    });
  }

  /**
   * Único ponto que troca a configuração em edição: o JSON de sinais nasce
   * dela, e sobra de outra empresa nele seria salvo junto.
   */
  private setKitMatrix(kitMatrix: IKitMatrix | null): void {
    this.kitMatrix = kitMatrix ? this.normalize(kitMatrix) : null;

    const routing: Partial<Record<RoutingField, unknown>> = {};
    (Object.keys(ROUTING_FIELDS) as RoutingField[]).forEach((field) => {
      if (kitMatrix?.[field] != null) {
        routing[field] = kitMatrix[field];
      }
    });

    this.routingJson = Object.keys(routing).length ? JSON.stringify(routing, null, 2) : '';
    this.routingJsonError = null;
  }

  onRoutingJsonChanged(text: string): void {
    this.routingJson = text;
    this.routingJsonError = this.parseRoutingJson(text).error;
  }

  /** Só a forma: o significado (sinais, opções, produtos) o backend valida. */
  private parseRoutingJson(text: string): { fields: Partial<IKitMatrix>; error: string | null } {
    if (!text.trim()) {
      return { fields: {}, error: null };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { fields: {}, error: 'JSON inválido. Confira vírgulas, aspas e chaves.' };
    }

    if (!isPlainObject(parsed)) {
      return { fields: {}, error: 'O JSON precisa ser um objeto com columnSignals, signals, needs e/ou swaps.' };
    }

    for (const key of Object.keys(parsed)) {
      if (!(key in ROUTING_FIELDS)) {
        return { fields: {}, error: `Campo "${key}" não é aceito aqui. Use columnSignals, signals, needs ou swaps.` };
      }

      const expectsList = ROUTING_FIELDS[key as RoutingField];
      const value = parsed[key];
      if (expectsList ? !Array.isArray(value) : !isPlainObject(value)) {
        return { fields: {}, error: `"${key}" precisa ser ${expectsList ? 'uma lista [...]' : 'um objeto {...}'}.` };
      }
    }

    return { fields: parsed as Partial<IKitMatrix>, error: null };
  }

  /**
   * O backend pode devolver campos ausentes. Preencher aqui evita espalhar
   * `?.` e `|| []` por todo o template.
   */
  private normalize(kitMatrix: IKitMatrix): IKitMatrix {
    return {
      routines: kitMatrix.routines || [],
      matrix: kitMatrix.matrix || {},
      kits: (kitMatrix.kits || []).map((kit) => ({
        ...kit,
        productIds: kit.productIds || [],
        price: kit.price ?? null,
        linkUrl: kit.linkUrl ?? null
      })),
      fixedNeeds: kitMatrix.fixedNeeds || [],
      ageRule: kitMatrix.ageRule || null,
      questions: {
        needByOptionId: kitMatrix.questions?.needByOptionId || {},
        routineByOptionId: kitMatrix.questions?.routineByOptionId || {},
        ageByOptionId: kitMatrix.questions?.ageByOptionId || {}
      }
    };
  }

  private flattenOptions(questions: ISkincareQuestion[]): IQuestionOptionChoice[] {
    return questions
      .filter((question) => question.active)
      .flatMap((question) =>
        (question.options || [])
          .filter((option) => option.active)
          .map((option) => ({
            optionId: String(option.id),
            label: `${question.text} — ${option.text}`
          }))
      );
  }

  // ---- modo ----

  enableKitEngine(): void {
    this.setKitMatrix({ ...createEmptyKitMatrix(), routines: ['basico', 'intermediario', 'completo'] });
  }

  removeKitEngine(): void {
    const companyId = this.selectedCompanyId;
    if (!companyId) {
      return;
    }

    this.isRemoving = true;

    this.catalogoProdutosService.removerKitMatrix(companyId).subscribe({
      next: () => {
        this.setKitMatrix(null);
        this.validationProblems = [];
        this.isRemoving = false;
        this.toastService.toast('Esta empresa voltou a usar o motor padrão.', EMessageType.Success);
      },
      error: () => {
        this.isRemoving = false;
        this.toastService.toast('Não foi possível remover a configuração de kits.', EMessageType.Error);
      }
    });
  }

  // ---- rotinas (colunas) ----

  addRoutine(): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.routines = [...this.kitMatrix.routines, ''];
    this.needs.forEach((need) => {
      this.kitMatrix!.matrix[need] = [...this.kitMatrix!.matrix[need], ''];
    });
  }

  removeRoutine(index: number): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.routines = this.kitMatrix.routines.filter((_, i) => i !== index);
    this.needs.forEach((need) => {
      this.kitMatrix!.matrix[need] = this.kitMatrix!.matrix[need].filter((_, i) => i !== index);
    });

    this.realignAgeRuleAfterRoutineRemoved(index);
  }

  /**
   * A regra de idade guarda POSIÇÕES de coluna. Remover uma coluna à esquerda
   * faria as mesmas posições passarem a significar outras colunas — a regra
   * mudaria de sentido em silêncio, e ainda dentro da faixa válida, então o
   * backend aceitaria. Reancorar, e desistir da regra quando a coluna que ela
   * usava foi a removida.
   */
  private realignAgeRuleAfterRoutineRemoved(removedIndex: number): void {
    const rule = this.kitMatrix?.ageRule;

    if (!rule) {
      return;
    }

    if (rule.fromColumn === removedIndex || rule.toColumn === removedIndex) {
      this.kitMatrix!.ageRule = null;
      this.toastService.toast(
        'A exceção por idade foi removida: ela usava a coluna que você apagou.',
        EMessageType.Warning
      );
      return;
    }

    if (rule.fromColumn > removedIndex) {
      rule.fromColumn -= 1;
    }

    if (rule.toColumn > removedIndex) {
      rule.toColumn -= 1;
    }
  }

  onRoutineRenamed(index: number, value: string): void {
    if (!this.kitMatrix) {
      return;
    }

    const previous = this.kitMatrix.routines[index];
    this.kitMatrix.routines[index] = value;

    // O mapa de opções aponta para o nome da rotina, não para a posição.
    Object.keys(this.kitMatrix.questions.routineByOptionId).forEach((optionId) => {
      if (this.kitMatrix!.questions.routineByOptionId[optionId] === previous) {
        this.kitMatrix!.questions.routineByOptionId[optionId] = value;
      }
    });
  }

  // ---- necessidades (linhas) ----

  addNeed(name: string): void {
    const trimmed = (name || '').trim();

    if (!this.kitMatrix || !trimmed || this.kitMatrix.matrix[trimmed]) {
      return;
    }

    this.kitMatrix.matrix[trimmed] = this.kitMatrix.routines.map(() => '');
  }

  removeNeed(need: string): void {
    if (!this.kitMatrix) {
      return;
    }

    delete this.kitMatrix.matrix[need];
    this.kitMatrix.fixedNeeds = this.kitMatrix.fixedNeeds.filter((fixed) => fixed !== need);
  }

  isFixedNeed(need: string): boolean {
    return (this.kitMatrix?.fixedNeeds || []).includes(need);
  }

  toggleFixedNeed(need: string): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.fixedNeeds = this.isFixedNeed(need)
      ? this.kitMatrix.fixedNeeds.filter((fixed) => fixed !== need)
      : [...this.kitMatrix.fixedNeeds, need];
  }

  setCell(need: string, columnIndex: number, kitId: string): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.matrix[need][columnIndex] = kitId;
  }

  // ---- kits ----

  addKit(): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.kits = [...this.kitMatrix.kits, { id: '', name: '', price: null, linkUrl: null, productIds: [] }];
  }

  /**
   * As células da matriz guardam o id do kit, não sua posição. Renomear o id
   * sem reapontar as células deixa cada uma órfã: some das opções do select
   * (que nasce de `kitMatrix.kits`) e o backend recusa o save apontando para
   * um kit que "não existe" — sem dizer que a causa foi este rename.
   */
  onKitIdRenamed(kitIndex: number, value: string): void {
    if (!this.kitMatrix) {
      return;
    }

    const previous = this.kitMatrix.kits[kitIndex].id;
    this.kitMatrix.kits[kitIndex].id = value;

    // Kit recém-criado nasce com id vazio, e célula não preenchida também é
    // vazia: reapontar a partir de `''` carimbaria o id novo em toda célula
    // em branco da matriz.
    if (!previous || previous === value) {
      return;
    }

    this.needs.forEach((need) => {
      this.kitMatrix!.matrix[need] = this.kitMatrix!.matrix[need].map((kitId) => (kitId === previous ? value : kitId));
    });
  }

  removeKit(index: number): void {
    if (!this.kitMatrix) {
      return;
    }

    const removed = this.kitMatrix.kits[index];
    this.kitMatrix.kits = this.kitMatrix.kits.filter((_, i) => i !== index);

    // Célula que apontava para o kit removido fica vazia, e o backend recusa
    // salvar assim — melhor do que apontar para um kit que sumiu.
    this.needs.forEach((need) => {
      this.kitMatrix!.matrix[need] = this.kitMatrix!.matrix[need].map((kitId) => (kitId === removed?.id ? '' : kitId));
    });
  }

  moveProductUp(kit: IKit, index: number, list: KitProductList = 'productIds'): void {
    if (index <= 0) {
      return;
    }

    const reordered = [...(kit[list] || [])];
    [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
    kit[list] = reordered;
  }

  moveProductDown(kit: IKit, index: number, list: KitProductList = 'productIds'): void {
    const current = kit[list] || [];
    if (index >= current.length - 1) {
      return;
    }

    const reordered = [...current];
    [reordered[index], reordered[index + 1]] = [reordered[index + 1], reordered[index]];
    kit[list] = reordered;
  }

  addProductToKit(kit: IKit, productId: string, list: KitProductList = 'productIds'): void {
    const current = kit[list] || [];
    if (!productId || current.includes(productId)) {
      return;
    }

    kit[list] = [...current, productId];
  }

  removeProductFromKit(kit: IKit, index: number, list: KitProductList = 'productIds'): void {
    const remaining = (kit[list] || []).filter((_, i) => i !== index);

    // Diurna/Noturna vazia volta a ser ausente: kit sem elas salva como antes.
    if (list !== 'productIds' && !remaining.length) {
      delete kit[list];
      return;
    }

    kit[list] = remaining;
  }

  productName(productId: string | undefined): string {
    if (!productId) {
      return '(produto sem identificador)';
    }

    const product = this.catalogProducts.find((item) => item.id === productId);

    if (!product) {
      return `${productId} (fora do catálogo)`;
    }

    return [product.brandName, product.name].filter(Boolean).join(' ') || productId;
  }

  // ---- regra de idade ----

  get hasAgeRule(): boolean {
    return !!this.kitMatrix?.ageRule;
  }

  toggleAgeRule(): void {
    if (!this.kitMatrix) {
      return;
    }

    this.kitMatrix.ageRule = this.kitMatrix.ageRule ? null : { triggerAgeValues: [], fromColumn: 0, toColumn: 1 };
  }

  // ---- mapa de opções ----

  optionMapEntries(kind: OptionMapKind): { optionId: string; value: string }[] {
    const map = this.optionMap(kind);
    return Object.keys(map).map((optionId) => ({ optionId, value: map[optionId] }));
  }

  /**
   * `optionMapEntries` devolve objetos novos a cada ciclo. Sem isso o ngFor
   * recria as linhas a cada change detection, e o click no "×" cai num botão
   * que já saiu do DOM.
   */
  trackByOptionId(_index: number, entry: { optionId: string }): string {
    return entry.optionId;
  }

  optionMap(kind: OptionMapKind): Record<string, string> {
    const questions = this.kitMatrix!.questions;

    if (kind === 'need') return questions.needByOptionId;
    if (kind === 'routine') return questions.routineByOptionId;
    return questions.ageByOptionId;
  }

  addOptionMapping(kind: OptionMapKind, optionId: string, value: string): void {
    if (!this.kitMatrix || !optionId || !value) {
      return;
    }

    this.optionMap(kind)[optionId] = value;
  }

  removeOptionMapping(kind: OptionMapKind, optionId: string): void {
    delete this.optionMap(kind)[optionId];
  }

  optionLabel(optionId: string): string {
    return (
      this.questionOptions.find((option) => option.optionId === optionId)?.label ??
      `Opção ${optionId} (fora do questionário)`
    );
  }

  /** Faixas etárias já mapeadas — é de onde a regra de idade escolhe os gatilhos. */
  get mappedAgeValues(): string[] {
    return Array.from(new Set(Object.values(this.kitMatrix?.questions.ageByOptionId || {})));
  }

  isAgeTrigger(ageValue: string): boolean {
    return (this.kitMatrix?.ageRule?.triggerAgeValues || []).includes(ageValue);
  }

  toggleAgeTrigger(ageValue: string): void {
    const rule = this.kitMatrix?.ageRule;
    if (!rule) {
      return;
    }

    rule.triggerAgeValues = this.isAgeTrigger(ageValue)
      ? rule.triggerAgeValues.filter((value) => value !== ageValue)
      : [...rule.triggerAgeValues, ageValue];
  }

  // ---- salvar ----

  save(): void {
    const companyId = this.selectedCompanyId;

    if (!companyId || !this.kitMatrix) {
      return;
    }

    const routing = this.parseRoutingJson(this.routingJson);
    if (routing.error) {
      this.routingJsonError = routing.error;
      return;
    }

    this.isSaving = true;
    this.validationProblems = [];

    this.catalogoProdutosService.salvarKitMatrix(companyId, { ...this.kitMatrix, ...routing.fields }).subscribe({
      next: (response) => {
        this.isSaving = false;

        const errors = (response?.messages || [])
          .filter((message) => message.type === EMessageType.Error)
          .map((message) => message.description)
          .filter((description): description is string => !!description);

        if (errors.length > 0) {
          // O backend recusa a configuração inteira quando algo não fecha.
          // Mostrar o que corrigir vale mais do que um "erro ao salvar".
          this.validationProblems = errors;
          return;
        }

        this.toastService.toast('Configuração de kits salva.', EMessageType.Success);
      },
      error: () => {
        this.isSaving = false;
        this.toastService.toast('Não foi possível salvar a configuração de kits.', EMessageType.Error);
      }
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
